import {
  Button,
  Grid,
  TextField,
  Paper,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Typography,
} from '@mui/material';

import WindowAnalyzerSelector from './WindowAnalyzerSelector';
import { WallForm, WindowForm } from './types';
import WindowComponent from '../windows/Window';
import { Controller, useForm } from 'react-hook-form';
import { grey } from '@mui/material/colors';

import { useWall } from 'features/walls/api/getWall';
import { useUpdateWall } from 'features/walls/api/updateWall';
import { useWindows } from 'features/windows/api/getWindows';
import { useUpdateWindow } from 'features/windows/api/updateWindow';
import { useDeleteWindow } from 'features/windows/api/deleteWindow';
import { useCreateWindow } from 'features/windows/api/createWindow';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsAdmin } from 'features/auth/hooks/useIsAdmin';

const WallOrganizer = () => {
  const pathName = useLocation().pathname;
  const splitPath = pathName.split('/');
  const wallId = Number(splitPath[splitPath.length - 1]);
  const isAdmin = useIsAdmin();
  const { data: wall } = useWall({ wallId });
  const updateWallMutation = useUpdateWall({});
  const updateWindowMutation = useUpdateWindow({});
  const deleteWindowMutation = useDeleteWindow({
    wallId,
  });
  const createWindowMutation = useCreateWindow({
    wallId,
  });
  const { data: windows } = useWindows({ wallId });
  const [windowIndicesWithChanges, setWindowIndicesWithChanges] = useState<
    number[]
  >([]);
  const cantEditSynched = wall?.synched && !isAdmin;

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    reset,
    formState: { dirtyFields, isDirty, errors },
  } = useForm<WallForm>({
    defaultValues: {
      name: wall?.name || '',
      rows: wall?.rows || 1,
      columns: wall?.columns || 1,
      hide_labels: wall?.hide_labels || false,
      windows: windows ? windows : [],
      selectedWindow: null,
      synched: false,
    },
  });

  useEffect(() => {
    if (wall)
      reset({
        ...wall,
        selectedWindow: null,
        hide_labels: !!wall.hide_labels,
        hide_detector_names: !!wall.hide_detector_names,
        synched: !!wall.synched,
      });
  }, [reset, wall]);

  const onSubmit = async () => {
    if (!wall) return;
    const formState = getValues();
    if (
      dirtyFields.columns ||
      dirtyFields.rows ||
      dirtyFields.name ||
      dirtyFields.hide_labels ||
      dirtyFields.hide_detector_names ||
      dirtyFields.synched
    ) {
      const updatedWall = {
        ...wall,
        name: formState.name,
        rows: formState.rows,
        columns: formState.columns,
        hide_labels: formState.hide_labels,
        hide_detector_names: formState.hide_detector_names,
        synched: formState.synched,
      };
      await updateWallMutation.mutateAsync({
        data: updatedWall,
        wallId: updatedWall.id,
      });
    }
    if (dirtyFields.windows) {
      for (const windowIndex of windowIndicesWithChanges) {
        const window = formState.windows[windowIndex];
        if (window.id)
          if (
            !window.analyzer_id ||
            window.analyzer_id === -1 ||
            !window.snapshot_id ||
            window.snapshot_id === -1
          )
            await deleteWindowMutation.mutateAsync(window.id);
          else
            await updateWindowMutation.mutateAsync({
              data: window,
              windowId: window.id,
            });
        else {
          if (
            window.analyzer_id &&
            window.snapshot_id &&
            window.analyzer_id !== -1 &&
            window.snapshot_id !== -1
          )
            await createWindowMutation.mutateAsync({ data: window });
        }
      }
    }
    reset({}, { keepValues: true });
  };

  const handleWindowClicked = (row: number, column: number) => {
    if (wall?.synched && !isAdmin) return;
    const windows = watch('windows');
    const foundWindow = windows?.find(
      (window) => window.pos_x === row && window.pos_y === column
    );
    if (foundWindow) {
      const window = {
        id: foundWindow?.id,
        analyzer_id: foundWindow.analyzer_id,
        snapshot_id: foundWindow.snapshot_id,
        wall_id: wallId,
        pos_x: row,
        pos_y: column,
        trace_mode: foundWindow.trace_mode || 0,
      };
      setValue('selectedWindow', window);
    } else {
      setValue('selectedWindow', {
        pos_x: row,
        pos_y: column,
        trace_mode: 0,
        analyzer_id: -1,
        snapshot_id: -1,
        wall_id: wallId,
      });
    }
  };

  const updateWindow = (window: WindowForm) => {
    const windows = watch('windows');
    const windowIndex = windows?.findIndex(
      (possibleMatch) =>
        possibleMatch.pos_x === window.pos_x &&
        possibleMatch.pos_y === window.pos_y
    );
    const newWindows: WindowForm[] = [...windows];
    if (windowIndex !== -1) newWindows[windowIndex] = window;
    else newWindows.push(window);
    setValue('windows', newWindows, { shouldDirty: true });
    const windowIndexToAddToChangeList =
      windowIndex !== -1 ? windowIndex : newWindows.length - 1;
    if (!windowIndicesWithChanges.includes(windowIndexToAddToChangeList)) {
      setWindowIndicesWithChanges([
        ...windowIndicesWithChanges,
        windowIndexToAddToChangeList,
      ]);
    }
  };

  return (
    <>
      <WindowAnalyzerSelector
        handleClose={() => setValue('selectedWindow', null)}
        window={watch('selectedWindow')}
        updateWindow={updateWindow}
      />
      {!cantEditSynched && (
        <Grid container alignItems='center' spacing={2}>
          <Controller
            name='name'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <TextField
                  id='name'
                  name='name'
                  label='Name'
                  value={value}
                  onChange={onChange}
                  error={!!errors.name}
                />
              </Grid>
            )}
          />
          <Controller
            name='rows'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <TextField
                  id='rows'
                  name='rows'
                  label='Rows'
                  type='number'
                  value={value}
                  onChange={(e) => {
                    if (Number(e.target.value) > 10)
                      onChange({ target: { value: 10 } });
                    else onChange(e);
                  }}
                  style={{ width: '5rem' }}
                  error={!!errors.rows}
                  inputProps={{ max: 10, min: 1 }}
                />
              </Grid>
            )}
          />
          <Controller
            name='columns'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <TextField
                  id='columns'
                  name='columns'
                  label='Columns'
                  value={value}
                  onChange={(e) => {
                    if (Number(e.target.value) > 10)
                      onChange({ target: { value: 10 } });
                    else if (Number(e.target.value) > 0) onChange(e);
                    else if (e.target.value === '') onChange(e);
                    else if (Number(e.target.value) < 1)
                      onChange({ target: { value: 1 } });
                    else onChange({ target: { value: '' } });
                  }}
                  type='number'
                  inputProps={{ max: 10, min: 1 }}
                  style={{ width: '5rem' }}
                  error={!!errors.columns}
                />
              </Grid>
            )}
          />
          <Controller
            name='hide_labels'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='hide_labels'
                        checked={!!value}
                        onChange={onChange}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                    label='Hide Labels'
                  />
                </FormGroup>
              </Grid>
            )}
          />
          <Controller
            name='hide_detector_names'
            control={control}
            render={({ field: { onChange, value } }) => (
              <Grid item>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='hide_detector_names'
                        checked={!!value}
                        onChange={onChange}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                    label='Hide Detector Names'
                  />
                </FormGroup>
              </Grid>
            )}
          />
          {isAdmin && (
            <Controller
              name='synched'
              control={control}
              render={({ field: { onChange, value } }) => (
                <Grid item>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name='synched'
                          checked={!!value}
                          onChange={onChange}
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      }
                      label='Make Global and Sync'
                    />
                  </FormGroup>
                </Grid>
              )}
            />
          )}
          <Grid item xs={2}>
            <Button
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              color='primary'
              variant='contained'
              fullWidth
              disabled={!isDirty}
            >
              Save
            </Button>
          </Grid>
          <Grid item>
            <Typography color={grey[500]}>Click a window to edit.</Typography>
          </Grid>
          <Grid item xs={12} />
        </Grid>
      )}
      {cantEditSynched && (
        <Grid
          container
          alignItems='center'
          justifyContent='center'
          sx={{ mb: 1 }}
          spacing={1}
        >
          <Typography color={grey[500]}>
            Global walls may only be edited by an admin.
          </Typography>
        </Grid>
      )}
      {[...Array(Number(watch('rows')) || 1)].map((row, rowIndex) => (
        <Grid
          key={rowIndex}
          container
          columns={watch('columns') ? Number(watch('columns')) : 1}
        >
          {[...Array(watch('columns') ? Number(watch('columns')) : 1)].map(
            (column, columnIndex) => (
              <Grid
                item
                xs={1}
                component={Paper}
                key={`column_${columnIndex}_row_${rowIndex}_wall_${wallId}_columns_${watch(
                  'columns'
                )}_rows_${watch('rows')}`}
                style={{ position: 'relative' }}
              >
                <WindowComponent
                  wallId={wallId}
                  disableTraces
                  row={rowIndex}
                  column={columnIndex}
                  handleWindowClicked={handleWindowClicked}
                  windowsOverride={getValues('windows')}
                />
              </Grid>
            )
          )}
        </Grid>
      ))}
    </>
  );
};

export default WallOrganizer;
